import type { OperationalState } from '../api/client/ders'

/**
 * Control modes we have explicit translations and a chip treatment for. The API
 * surfaces `controlMode` as an open string, so anything outside this set
 * resolves to UNKNOWN — see {@link normalizeControlMode}.
 *
 * STOP and the two TEST modes are what a DeepSea generator actually reports
 * (polaroid decodes GenComm's mode register to "Stop mode", "Test on load mode",
 * "Test off load mode" among others). STOP especially is not exotic: it is how a
 * generator is shut down, so leaving it out rendered the most common non-running
 * state as "Unknown".
 *
 * TEST, with no load qualifier, is what a Sunbelt (DEIF AGC-150) reports: its
 * controller has one Test mode, selected remotely like Manual and Auto.
 *
 * STANDBY and MAINTENANCE are mode values the governed `set_control_mode`
 * vocabulary (gateway-schema `ModeValue`) lets a device advertise and report, so
 * a device in either state reads by name rather than as UNKNOWN. Neither permits
 * a start or a breaker close, so they join no predicate list below.
 *
 * REMOTE is kept although no OEM emits it today: `control_mode` is a base
 * `OperationalState` field as of polaroid v0.14.2, so any OEM can populate it,
 * and "Remote" is a plausible value for a non-DeepSea controller.
 *
 * Deliberately absent, and left to the UNKNOWN fallback — which echoes the raw
 * label, so they still read correctly on screen: "Auto with manual restore
 * (Prohibit Return)" and "User configuration mode". Both are rare and neither
 * has a sensible short label.
 *
 * "Auto with manual restore" *does* touch one interlock: it is a mode in which
 * the DeepSea will close its breaker. Rather than admit it to this vocabulary
 * for one predicate's sake, {@link isBreakerCloseableControlMode} fails open on
 * UNKNOWN — see there for why that is the safe direction.
 */
export const CONTROL_MODES = [
  'AUTO',
  'MANUAL',
  'REMOTE',
  'STOP',
  'TEST',
  'TEST_ON_LOAD',
  'TEST_OFF_LOAD',
  'OFF',
  'STANDBY',
  'MAINTENANCE',
  'UNKNOWN'
] as const
export type ControlMode = (typeof CONTROL_MODES)[number]

/**
 * Modes in which the device will action a start command.
 *
 * Mirrors the edge's own precondition rather than guessing: GenComm actions
 * "Start Engine" only in Manual or Test mode and **silently ignores** the key
 * otherwise, so the aryll plugin reads the mode first and rejects a start that
 * would do nothing (raw Page 3 values 2, 3 and 6). The UI disables the control
 * for the same reason, but a 422 can still come back — the mode can change
 * between render and send — so the send path must handle it too.
 */
export const STARTABLE_CONTROL_MODES: readonly ControlMode[] = ['MANUAL', 'TEST', 'TEST_ON_LOAD', 'TEST_OFF_LOAD']

/** `true` when a start command will actually be actioned in this mode. */
export function isStartableControlMode(raw?: string | null): boolean {
  return STARTABLE_CONTROL_MODES.includes(normalizeControlMode(raw))
}

/**
 * Modes we can name in which the device will close the generator breaker.
 *
 * Not the same set as {@link STARTABLE_CONTROL_MODES}, in both directions. The
 * edge permits Manual, Test on load and Auto with manual restore (raw Page 3
 * values 2, 3 and 4); Test *off* load is excluded, because a set testing off
 * load is by definition not taking load.
 *
 * Auto with manual restore is missing from this list only because it is missing
 * from {@link CONTROL_MODES} — hence the UNKNOWN handling in
 * {@link isBreakerCloseableControlMode}.
 */
export const BREAKER_CLOSEABLE_CONTROL_MODES: readonly ControlMode[] = ['MANUAL', 'TEST_ON_LOAD']

/**
 * `true` when a close-breaker command is worth offering in this mode.
 *
 * Fails **open** on UNKNOWN, unlike {@link isStartableControlMode}, and that is
 * deliberate. "Auto with manual restore" is a mode the edge will action but this
 * vocabulary cannot name, so it normalizes to UNKNOWN — a closed default would
 * grey out the button on a generator perfectly willing to close, with a reason
 * line that flatly contradicts the mode chip beside it. Failing open costs at
 * worst a rejection carrying the controller's own explanation, which is a far
 * better error than an unexplained dead button.
 *
 * Safe because this is an affordance, not a guard: the edge re-reads the mode
 * before writing and refuses anything it would only silently ignore.
 */
export function isBreakerCloseableControlMode(raw?: string | null): boolean {
  const mode = normalizeControlMode(raw)
  return mode === 'UNKNOWN' || BREAKER_CLOSEABLE_CONTROL_MODES.includes(mode)
}

/**
 * The canonical mode family a reported control mode belongs to, spelled as the
 * control-mode values a manifest's `requires.controlModes` names (gateway-schema
 * `ModeValue`): the three Test spellings are one `test` family, since a hint that
 * asks for Test is satisfied by Test on load and Test off load alike.
 *
 * `null` for REMOTE, UNKNOWN and anything the vocabulary cannot name: there is no
 * family to test membership against, so callers must decide what an unreadable mode
 * means (the manifest interlock fails open).
 */
const CONTROL_MODE_FAMILY: Partial<Record<ControlMode, string>> = {
  MANUAL: 'manual',
  AUTO: 'auto',
  STOP: 'stop',
  OFF: 'off',
  STANDBY: 'standby',
  MAINTENANCE: 'maintenance',
  TEST: 'test',
  TEST_ON_LOAD: 'test',
  TEST_OFF_LOAD: 'test'
}

/** The mode family a reported control mode belongs to, or `null` when it maps to none. */
export function controlModeFamily(raw?: string | null): string | null {
  return CONTROL_MODE_FAMILY[normalizeControlMode(raw)] ?? null
}

/**
 * Reads the controller's own sequence state ("Ready", "Starting", "Rated", "Stop
 * Normal"…) off a DER's operational state, as the device names it. It says whether
 * the engine is running regardless of load, which the power-derived status cannot:
 * a running engine with no load reads Idle there. Whitespace-only values are absent.
 */
export function readControllerMode(operationalState?: OperationalState | null): string | null {
  const raw = operationalState?.controllerMode?.trim()
  return raw ? raw : null
}

/**
 * Reads the raw `controlMode` string off a DER's operational state. Whitespace-only
 * values are treated as absent so the caller's "unknown" path handles them.
 */
export function readControlMode(operationalState?: OperationalState | null): string | null {
  const raw = operationalState?.controlMode?.trim()
  return raw ? raw : null
}

/**
 * Collapses the API's open `controlMode` string onto our known vocabulary.
 *
 * Tolerant of both the human-readable spelling the API is expected to publish
 * ("Auto mode") and the SCREAMING_SNAKE spelling the rest of the snapshot enums
 * use ("AUTO_MODE"): the two differ only in case and separators, and pinning the
 * UI to one would make the chip silently read "Unknown" if the backend ships the
 * other. The trailing `_MODE` is redundant once we've matched the mode itself.
 */
export function normalizeControlMode(raw?: string | null): ControlMode {
  const canonical = canonicalize(raw)
  return canonical && isControlMode(canonical) ? canonical : 'UNKNOWN'
}

/**
 * `true` when the raw value maps onto a mode we have a translation for
 * (including an explicit "Unknown"). Consumers that echo unrecognized strings
 * verbatim — so a new backend mode still reads correctly before its translation
 * ships — use this to tell "not a mode we know" apart from "known to be unknown".
 */
export function isKnownControlMode(raw?: string | null): boolean {
  const canonical = canonicalize(raw)
  return canonical != null && isControlMode(canonical)
}

/**
 * `true` when the value is worth putting on screen — i.e. we either recognize it
 * or it carries its own raw label. A missing value, or one the device explicitly
 * reports as "Unknown", is not: a chip reading "Unknown" with no further context
 * tells an operator nothing, so consumers hide the affordance instead.
 */
export function isDisplayableControlMode(raw?: string | null): boolean {
  const canonical = canonicalize(raw)
  return canonical != null && canonical !== 'UNKNOWN'
}

function canonicalize(raw?: string | null): string | null {
  const trimmed = raw?.trim()
  if (!trimmed) return null
  return (
    trimmed
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      // Trailing separators first: a label ending in punctuation ("Auto with
      // manual restore (Prohibit Return)") would otherwise canonicalize with a
      // dangling underscore, and `_MODE$` would not match a value like
      // "TEST_ON_LOAD_MODE_" either.
      .replace(/_+$/, '')
      .replace(/_MODE$/, '')
      .replace(/_+$/, '')
  )
}

function isControlMode(value: string): value is ControlMode {
  return (CONTROL_MODES as readonly string[]).includes(value)
}
